import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { Inbox, Search, Wallet } from "lucide-react";
import {
  filterGroupSchema,
  parseFilterFromSearchParam,
  visibleColumnKeys,
  type FilterTree,
} from "@colab/shared";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import {
  ActiveViewColumnsButton,
  AddFilterMenu,
  SavedViewTabs,
} from "@/components/shared/saved-views";
import { useViews } from "@/features/admin/views/use-views";
import { useActiveView } from "@/features/admin/views/active-view";
import { useFunds } from "./use-funds";
import { useMdfRequests } from "./use-mdf-requests";
import { MdfRequestsTable } from "./requests-table";
import { RequestDrawer } from "./request-drawer";

// Phase 7 sub-bucket 17 — admin MDF requests queue + first Saved Views
// consumer. Builds on the sub-bucket 15 chip toolbar + drawer-as-
// shareable-route (`?id=`) by adding:
//
//   - `<SavedViewTabs entityType="mdf_request">` strip above the toolbar.
//   - `<AddFilterMenu>` for ad-hoc FilterTree edits — writes `?filter=` URL
//     state OR PATCHes the active saved view (when the user can edit it).
//   - `useMdfRequests({ filterNode })` — when a saved view is active OR a
//     URL filter is set, the chip toolbar hides and the FilterTree drives
//     the worker read via the new `?filter=<base64-json>` channel.
//
// Column visibility (ConfigureColumns) deliberately ships in the
// primitives but is NOT mounted on this page: the existing
// `MdfRequestsTable` doesn't yet honour `SavedView.columns`. A future
// sub-bucket can extend the table + drop ConfigureColumns into the
// toolbar in one pass across all admin queues.

const ENTITY_TYPE = "mdf_request" as const;

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "spent", label: "Spent" },
  { value: "declined", label: "Declined" },
  { value: "cancelled", label: "Cancelled" },
];

const ALL_FUNDS = "__all__";

export function MdfRequestsQueuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get("status") ?? "all";
  const fundId = searchParams.get("fund") ?? ALL_FUNDS;
  const q = searchParams.get("q") ?? "";
  const selectedId = searchParams.get("id");

  // ---- Saved Views resolution ----
  const viewsQuery = useViews(ENTITY_TYPE);
  const active = useActiveView(viewsQuery.data);
  const urlFilter = useMemo<FilterTree | null>(() => {
    const raw = searchParams.get("filter");
    if (!raw) return null;
    let decoded: string | null = null;
    try {
      decoded = atob(raw);
    } catch {
      decoded = raw;
    }
    const parsed = parseFilterFromSearchParam(decoded);
    if (parsed === null) return null;
    const validated = filterGroupSchema.safeParse(parsed);
    return validated.success ? validated.data : null;
  }, [searchParams]);

  const effectiveFilter = urlFilter ?? active.filter;
  const filterIsOverride = urlFilter !== null && active.filter !== null;
  const filterDriving = effectiveFilter !== null;

  function updateParam(key: string, value: string | null) {
    const sp = new URLSearchParams(searchParams);
    if (
      value === null ||
      value === "" ||
      value === "all" ||
      value === ALL_FUNDS
    ) {
      sp.delete(key);
    } else {
      sp.set(key, value);
    }
    setSearchParams(sp, { replace: true });
  }

  function setFilterInUrl(tree: FilterTree | null) {
    const sp = new URLSearchParams(searchParams);
    if (tree) {
      sp.set("filter", btoa(JSON.stringify(tree)));
    } else {
      sp.delete("filter");
    }
    setSearchParams(sp, { replace: true });
  }

  const fundsQuery = useFunds();
  const requestsQuery = useMdfRequests({
    // Chip filters short-circuit when a FilterTree drives the read —
    // mutually exclusive, no double-filtering.
    status: filterDriving ? null : status === "all" ? null : status,
    fundId: filterDriving ? null : fundId === ALL_FUNDS ? null : fundId,
    filterNode: effectiveFilter,
  });

  const activeVisible = useMemo(
    () => visibleColumnKeys(active.columns, ENTITY_TYPE),
    [active.columns],
  );

  // Search predicate stays client-side — Phase A volume is sub-1k rows;
  // a SQL ilike on partner_name (across a join) is annoying to write and
  // wouldn't change anything user-visible at this scale.
  const filteredRows = useMemo(() => {
    const rows = requestsQuery.data ?? [];
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        r.partner_name.toLowerCase().includes(term) ||
        r.campaign_name.toLowerCase().includes(term),
    );
  }, [requestsQuery.data, q]);

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            MDF requests
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Partner-submitted marketing requests across every fund. Approve,
            decline, or mark spent from the row drawer.
          </p>
        </div>
      </header>

      <SavedViewTabs
        entityType={ENTITY_TYPE}
        saveInitialState={{
          filter: effectiveFilter,
          columns: active.columns,
          sort: active.sort,
        }}
      />

      {filterDriving ? (
        <FilterToolbar
          entityType={ENTITY_TYPE}
          effectiveFilter={effectiveFilter}
          isOverride={filterIsOverride}
          onApplyFilter={setFilterInUrl}
          q={q}
          onQChange={(next) => updateParam("q", next)}
          activeView={active.view}
        />
      ) : (
        <ChipToolbar
          entityType={ENTITY_TYPE}
          status={status}
          onStatusChange={(s) => updateParam("status", s)}
          fundId={fundId}
          onFundChange={(f) => updateParam("fund", f)}
          funds={fundsQuery.data ?? []}
          q={q}
          onQChange={(next) => updateParam("q", next)}
          onApplyFilter={setFilterInUrl}
          activeView={active.view}
        />
      )}

      <div className="mt-4">
        {requestsQuery.isLoading ? (
          <Skeleton className="h-40 w-full rounded-xl" />
        ) : requestsQuery.isError ? (
          <EmptyState
            icon={Inbox}
            title="Couldn't load requests"
            description={requestsQuery.error?.message ?? "Try refreshing."}
          />
        ) : filteredRows.length > 0 ? (
          <MdfRequestsTable
            rows={filteredRows}
            onSelect={(id) => updateParam("id", id)}
            selectedId={selectedId}
            showFundColumn
            visibleColumns={activeVisible.length > 0 ? activeVisible : undefined}
          />
        ) : (
          <EmptyState
            icon={
              q || filterDriving || status !== "all" || fundId !== ALL_FUNDS
                ? Search
                : Inbox
            }
            title={
              q || filterDriving || status !== "all" || fundId !== ALL_FUNDS
                ? "No requests match this view"
                : "No requests yet"
            }
            description={
              q || filterDriving || status !== "all" || fundId !== ALL_FUNDS
                ? "Try clearing the search box, status chip, or fund filter."
                : "When partners submit MDF requests, they'll land here for review."
            }
          />
        )}
      </div>

      <RequestDrawer
        requestId={selectedId}
        onClose={() => updateParam("id", null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chip-driven toolbar (no saved view, no URL filter).
// ---------------------------------------------------------------------------

function ChipToolbar({
  entityType,
  status,
  onStatusChange,
  fundId,
  onFundChange,
  funds,
  q,
  onQChange,
  onApplyFilter,
  activeView,
}: {
  entityType: typeof ENTITY_TYPE;
  status: string;
  onStatusChange: (next: string) => void;
  fundId: string;
  onFundChange: (next: string) => void;
  funds: Array<{ id: string; name: string }>;
  q: string;
  onQChange: (next: string) => void;
  onApplyFilter: (tree: FilterTree | null) => void;
  activeView: ReturnType<typeof useActiveView>["view"];
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex flex-wrap items-center gap-1 rounded-md border bg-card p-0.5">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onStatusChange(opt.value)}
            className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
              status === opt.value
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <Select value={fundId} onValueChange={onFundChange}>
        <SelectTrigger className="w-[200px]">
          <Wallet className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
          <SelectValue placeholder="All funds" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_FUNDS}>All funds</SelectItem>
          {funds.map((f) => (
            <SelectItem key={f.id} value={f.id}>
              {f.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <AddFilterMenu
        entityType={entityType}
        value={null}
        onApply={onApplyFilter}
      />

      <ActiveViewColumnsButton
        entityType={entityType}
        activeView={activeView}
      />

      <div className="relative ml-auto min-w-[220px] max-w-md flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => onQChange(e.target.value)}
          placeholder="Search campaign or partner"
          className="pl-8"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FilterTree-driven toolbar (saved view active OR URL filter set). The
// chip controls hide — the FilterTree IS the filter expression, and the
// AddFilterMenu lets the user edit it.
// ---------------------------------------------------------------------------

function FilterToolbar({
  entityType,
  effectiveFilter,
  isOverride,
  onApplyFilter,
  q,
  onQChange,
  activeView,
}: {
  entityType: typeof ENTITY_TYPE;
  effectiveFilter: FilterTree | null;
  isOverride: boolean;
  onApplyFilter: (tree: FilterTree | null) => void;
  q: string;
  onQChange: (next: string) => void;
  activeView: ReturnType<typeof useActiveView>["view"];
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <AddFilterMenu
        entityType={entityType}
        value={effectiveFilter}
        onApply={onApplyFilter}
        isOverride={isOverride}
      />

      <ActiveViewColumnsButton
        entityType={entityType}
        activeView={activeView}
      />

      <div className="relative ml-auto min-w-[220px] max-w-md flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => onQChange(e.target.value)}
          placeholder="Search campaign or partner"
          className="pl-8"
        />
      </div>
    </div>
  );
}
